/**
 * Suggests how to connect a mailbox from its address: by the address domain first, then (on the
 * server) by the domain's MX records. Detection only suggests the next step; the person always
 * confirms it, and nothing derived from DNS is ever used as a place to send a credential.
 */
import { UNSUPPORTED_MAIL_PROVIDERS, presetForDomain } from './mail-presets.js';

export type MailDetection =
  | { kind: 'oauth'; provider: 'google' | 'microsoft' | 'zoho' }
  | { kind: 'preset'; presetId: string }
  | { kind: 'spacemail' }
  | { kind: 'unsupported'; provider: string; reason: string }
  | { kind: 'custom' };

const GOOGLE_DOMAINS = ['gmail.com', 'googlemail.com'];
const MICROSOFT_DOMAINS = ['outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'passport.com', 'windowslive.com'];
// outlook.de, hotmail.co.uk, live.fr, …: Microsoft's consumer domains in other countries.
const MICROSOFT_COUNTRY = /^(outlook|hotmail|live)\.(com?\.)?[a-z]{2}$/;
const ZOHO_DOMAINS = ['zoho.com', 'zohomail.com', 'zoho.eu', 'zohomail.eu', 'zoho.in', 'zohomail.in', 'zoho.com.au', 'zohomail.com.au', 'zoho.jp', 'zohomail.jp', 'zohocloud.ca', 'zohomailcloud.ca', 'zoho.sa'];
const SPACEMAIL_DOMAINS = ['spacemail.com'];

/** The part after the last @, lowercased; null when the address has no plausible domain. */
export function emailDomain(email: string): string | null {
  const at = email.trim().lastIndexOf('@');
  if (at < 1) return null;
  const d = email.trim().slice(at + 1).toLowerCase().replace(/\.$/, '');
  return /^[a-z0-9.-]+\.[a-z0-9-]{2,}$/.test(d) && !d.includes('..') ? d : null;
}

function unsupported(id: string): MailDetection {
  const p = UNSUPPORTED_MAIL_PROVIDERS.find((u) => u.id === id)!;
  return { kind: 'unsupported', provider: p.label, reason: p.reason };
}

/** Detection from the address domain alone (no network). Null when the domain is not known. */
export function detectByDomain(domain: string): MailDetection | null {
  const d = domain.trim().toLowerCase();
  if (GOOGLE_DOMAINS.includes(d)) return { kind: 'oauth', provider: 'google' };
  if (MICROSOFT_DOMAINS.includes(d) || MICROSOFT_COUNTRY.test(d)) return { kind: 'oauth', provider: 'microsoft' };
  if (ZOHO_DOMAINS.includes(d)) return { kind: 'oauth', provider: 'zoho' };
  if (SPACEMAIL_DOMAINS.includes(d)) return { kind: 'spacemail' };
  const blocked = UNSUPPORTED_MAIL_PROVIDERS.find((u) => u.domains.includes(d));
  if (blocked) return unsupported(blocked.id);
  const p = presetForDomain(d);
  return p ? { kind: 'preset', presetId: p.id } : null;
}

type MxRule = { suffix: string; result: MailDetection } | { pattern: RegExp; result: MailDetection };

/** Mail-exchanger host names of hosted mail services, for addresses on a custom domain. */
const MX_RULES: readonly MxRule[] = [
  // Google Workspace: aspmx.l.google.com, alt1.aspmx.l.google.com, aspmx2.googlemail.com, smtp.google.com
  { suffix: 'google.com', result: { kind: 'oauth', provider: 'google' } },
  { suffix: 'googlemail.com', result: { kind: 'oauth', provider: 'google' } },
  // Microsoft 365: <tenant>.mail.protection.outlook.com; Outlook.com: *.olc.protection.outlook.com
  { suffix: 'protection.outlook.com', result: { kind: 'oauth', provider: 'microsoft' } },
  // Zoho Mail data centres: mx.zoho.com, mx.zoho.eu, mx.zoho.in, mx.zoho.com.au, mx.zoho.jp, mx.zohocloud.ca, mx.zoho.sa
  ...['zoho.com', 'zoho.eu', 'zoho.in', 'zoho.com.au', 'zoho.jp', 'zohocloud.ca', 'zoho.sa'].map((suffix) => ({ suffix, result: { kind: 'oauth', provider: 'zoho' } as MailDetection })),
  { suffix: 'spacemail.com', result: { kind: 'spacemail' } },
  { suffix: 'titan.email', result: { kind: 'preset', presetId: 'titan' } },
  { suffix: 'secureserver.net', result: { kind: 'preset', presetId: 'godaddy' } },
  { suffix: 'privateemail.com', result: { kind: 'preset', presetId: 'namecheap' } },
  { suffix: 'emailsrvr.com', result: { kind: 'preset', presetId: 'rackspace' } },
  { suffix: 'messagingengine.com', result: { kind: 'preset', presetId: 'fastmail' } },
  { suffix: 'mail.icloud.com', result: { kind: 'preset', presetId: 'icloud' } },
  { pattern: /^mta\d+\.am0\.yahoodns\.net$/, result: { kind: 'preset', presetId: 'yahoo' } },
  { pattern: /^mx-aol\.mail\.gm0\.yahoodns\.net$/, result: { kind: 'preset', presetId: 'aol' } },
  { suffix: 'hostinger.com', result: { kind: 'preset', presetId: 'hostinger' } },
  { suffix: 'ionos.com', result: { kind: 'preset', presetId: 'ionos' } },
  { suffix: 'mailbox.org', result: { kind: 'preset', presetId: 'mailboxorg' } },
  { suffix: 'yandex.net', result: { kind: 'preset', presetId: 'yandex' } },
  { suffix: 'yandex.ru', result: { kind: 'preset', presetId: 'yandex' } },
  { suffix: 'protonmail.ch', result: unsupported('proton') },
  { suffix: 'tutanota.de', result: unsupported('tuta') },
];

function matches(rule: MxRule, host: string): boolean {
  if ('pattern' in rule) return rule.pattern.test(host);
  return host === rule.suffix || host.endsWith(`.${rule.suffix}`);
}

/**
 * Detection from MX host names, most preferred first. The first MX that belongs to a known
 * service decides; unknown MX hosts mean a custom server.
 */
export function detectByMx(mxHosts: readonly string[]): MailDetection | null {
  for (const raw of mxHosts) {
    const host = raw.trim().toLowerCase().replace(/\.$/, '');
    const rule = MX_RULES.find((r) => matches(r, host));
    if (rule) return rule.result;
  }
  return null;
}
